import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router';
import { archiveRangeLabels, archiveRanges, type ArchiveRange } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, MenuItem, MenuList, Popover, Skeleton } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { ModuleEmptyPage } from '../../shell/ModulePages';
import { CalendarPage } from './CalendarModule';
import { useArchiveDetail, useCalendarArchive, type ArchiveItem } from './calendarApi';
import styles from './ArchivePage.module.css';

/** „128 Termine · Show-Quote 86 %" */
function statsLine(total: number, showRate: number | null): string {
  const count = `${total} ${total === 1 ? 'Termin' : 'Termine'}`;
  return showRate === null ? count : `${count} · Show-Quote ${Math.round(showRate)} %`;
}

interface FilterPillProps {
  label: string;
  tone: 'dark' | 'quiet';
  menuLabel: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}

/** The filter pills of Beratung 04 („Alle Berater" navy, „Letzte 30 Tage" sunken) with their popovers. */
function FilterPill({ label, tone, menuLabel, options, value, onChange }: FilterPillProps) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={clsx(styles.filterPill, tone === 'dark' ? styles.filterDark : styles.filterQuiet)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {label}
      </button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        placement="bottom-end"
        initialFocus="none"
        width={220}
      >
        <MenuList label={menuLabel} onClose={() => setOpen(false)}>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              label={option.label}
              active={option.value === value}
              onSelect={() => {
                setOpen(false);
                onChange(option.value);
              }}
            />
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

function statusPillClass(tone: ArchiveItem['status']['tone']): string | undefined {
  switch (tone) {
    case 'success':
      return styles.statusSuccess;
    case 'danger':
      return styles.statusDanger;
    case 'warning':
      return styles.statusWarning;
    case 'neutral':
      return styles.statusNeutral;
  }
}

function DeliveryPills({ delivery, expanded }: { delivery: ArchiveItem['delivery']; expanded: boolean }) {
  return (
    <span className={styles.pills}>
      {delivery.sent > 0 ? (
        <span className={clsx(styles.pill, styles.pillSent, expanded && styles.pillSentExpanded)}>
          {delivery.sent} gesendet
        </span>
      ) : null}
      {delivery.opened > 0 ? <span className={clsx(styles.pill, styles.pillOpened)}>geöffnet</span> : null}
      {delivery.failed > 0 ? (
        <span className={clsx(styles.pill, styles.pillFailed)}>{delivery.failed} Fehler</span>
      ) : null}
    </span>
  );
}

function resultClass(result: string): string | undefined {
  if (result.startsWith('geöffnet')) return styles.resultOpened;
  if (result.startsWith('Fehler')) return styles.resultFailed;
  return undefined;
}

/** The expanded row (`GET /calendar/archive/:id`): Gast / Meetingraum / Einwilligung / Quelle + „Zustell-Verlauf". */
function ArchiveDetailBlock({ id }: { id: string }) {
  const detail = useArchiveDetail(id);
  if (detail.isPending) {
    return (
      <div className={styles.detail} aria-busy="true">
        <Skeleton width="60%" height={14} />
        <Skeleton width="45%" height={14} />
        <Skeleton width="50%" height={14} />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className={styles.detail}>
        <Banner tone="danger">{errorMessage(detail.error)}</Banner>
      </div>
    );
  }
  const data = detail.data;
  const facts: Array<[string, string]> = [['Gast', data.guestLine]];
  if (data.roomLine) facts.push(['Meetingraum', data.roomLine]);
  if (data.consentLine) facts.push(['Einwilligung', data.consentLine]);
  facts.push(['Quelle', data.sourceLine]);
  return (
    <div className={styles.detail}>
      <dl className={styles.facts}>
        {facts.map(([label, value]) => (
          <div key={label} className={styles.fact}>
            <dt className={styles.factLabel}>{label}</dt>
            <dd className={styles.factValue}>{value}</dd>
          </div>
        ))}
      </dl>
      <div className={styles.historyTitle}>Zustell-Verlauf</div>
      {data.deliveries.length > 0 ? (
        <div className={styles.history}>
          {data.deliveries.map((entry, index) => (
            <div key={`${entry.at}-${index}`} className={styles.historyRow}>
              <span className={styles.historyAt}>{entry.atLabel}</span>
              <span className={styles.historyLabel}>{entry.label}</span>
              <span className={resultClass(entry.result)}>{entry.result}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.historyEmpty}>Keine Zustellungen.</div>
      )}
      <Link to={`/calendar/appointments/${id}`} className={styles.openLink}>
        Termin öffnen
      </Link>
    </div>
  );
}

function ArchiveRow({
  item,
  expanded,
  onToggle,
}: {
  item: ArchiveItem;
  expanded: boolean;
  onToggle: () => void;
}) {
  const detailId = `archive-detail-${item.id}`;
  function rowClick(event: MouseEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest('a, button')) return;
    onToggle();
  }
  return (
    <>
      <div className={clsx(styles.row, expanded && styles.rowExpanded)} onClick={rowClick}>
        <span className={styles.date}>{item.dateLabel}</span>
        <span className={styles.guestCell}>
          <button
            type="button"
            className={styles.guest}
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={onToggle}
          >
            {item.guest}
          </button>
        </span>
        <span className={styles.cell}>{item.eventTypeName}</span>
        <span className={styles.cell}>{item.advisorShort}</span>
        <span className={styles.relation}>{item.relation}</span>
        <DeliveryPills delivery={item.delivery} expanded={expanded} />
        <span className={styles.status}>
          <span className={clsx(styles.statusPill, statusPillClass(item.status.tone))}>
            {item.status.label}
          </span>
          {expanded ? (
            <button type="button" className={styles.subLink} onClick={onToggle}>
              Details schließen
            </button>
          ) : item.reviewHref && item.status.sub === 'Rückblick' ? (
            <Link to={item.reviewHref} className={styles.subLink}>
              Rückblick
            </Link>
          ) : item.status.sub ? (
            <span className={styles.sub}>{item.status.sub}</span>
          ) : null}
        </span>
      </div>
      {expanded ? (
        <div id={detailId} className={styles.detailWrap}>
          <ArchiveDetailBlock id={item.id} />
        </div>
      ) : null}
    </>
  );
}

/** Beratung 04 — `/calendar/archive`: appointments that are over or cancelled, one row expandable at a time. */
export function ArchivePage() {
  const mobile = useIsMobile();
  const [range, setRange] = useState<ArchiveRange>('30d');
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const archive = useCalendarArchive(range, advisorId);
  const sentinel = useRef<HTMLDivElement>(null);
  const first = archive.data?.pages[0];
  const items = archive.data?.pages.flatMap((page) => page.items) ?? [];
  const advisors = first?.advisors ?? [];
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = archive;

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) void fetchNextPage();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  const advisorLabel = advisors.find((entry) => entry.id === advisorId)?.shortName ?? 'Alle Berater';
  const filters = (
    <span className={styles.filters}>
      <FilterPill
        label={advisorLabel}
        tone="dark"
        menuLabel="Berater"
        options={[
          { value: '', label: 'Alle Berater' },
          ...advisors.map((entry) => ({ value: entry.id, label: entry.shortName })),
        ]}
        value={advisorId ?? ''}
        onChange={(value) => {
          setAdvisorId(value === '' ? null : value);
          setExpanded(null);
        }}
      />
      <FilterPill
        label={archiveRangeLabels[range]}
        tone="quiet"
        menuLabel="Zeitraum"
        options={archiveRanges.map((value) => ({ value, label: archiveRangeLabels[value] }))}
        value={range}
        onChange={(value) => {
          setRange(value as ArchiveRange);
          setExpanded(null);
        }}
      />
    </span>
  );
  const status = archive.error instanceof ApiError ? archive.error.status : null;

  return (
    <CalendarPage
      tab="archive"
      pill={null}
      actions={false}
      summary={
        first ? <span className={styles.summary}>{statsLine(first.total, first.showRate)}</span> : null
      }
      tabEnd={filters}
    >
      {archive.isError && !archive.data ? (
        <Banner
          tone="danger"
          className={styles.banner}
          actions={
            <Button
              variant="destructiveFilled"
              size="small"
              loading={archive.isFetching}
              onClick={() => void archive.refetch()}
            >
              Erneut laden
            </Button>
          }
        >
          Das Archiv konnte nicht geladen werden{status !== null ? ` · Fehler ${status}` : ''}.{' '}
          {errorMessage(archive.error)}
        </Banner>
      ) : !archive.data ? (
        <div className={styles.skeleton} aria-busy="true" aria-label="Archiv wird geladen">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} height={index === 0 ? 38 : 46} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <ModuleEmptyPage module="calendar" />
      ) : (
        <div className={clsx(styles.table, mobile && styles.cards)}>
          <div className={styles.headRow} aria-hidden={mobile || undefined}>
            <span>Datum</span>
            <span>Gast</span>
            <span>Ereignistyp</span>
            <span>Berater</span>
            <span>Bezug</span>
            <span>Zustellung</span>
            <span>Status</span>
          </div>
          {items.map((item) => (
            <ArchiveRow
              key={item.id}
              item={item}
              expanded={expanded === item.id}
              onToggle={() => setExpanded((current) => (current === item.id ? null : item.id))}
            />
          ))}
          <div ref={sentinel} className={styles.sentinel} aria-hidden="true" />
          {isFetchingNextPage ? (
            <div className={styles.more} aria-busy="true">
              <Skeleton height={46} />
            </div>
          ) : null}
        </div>
      )}
    </CalendarPage>
  );
}
